import {
    Box,
    InputAdornment,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    alpha,
    useTheme
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import GridViewIcon from "@mui/icons-material/GridView";
import ViewListIcon from "@mui/icons-material/ViewList";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import type { DashboardSort, DashboardView } from "./types";

const SORT_LABELS: Record<DashboardSort, string> = {
    updated: "Last updated",
    name: "A–Z",
    features: "Features",
    stories: "Stories"
};

const SORT_CYCLE: DashboardSort[] = ["updated", "name", "features", "stories"];

interface ProductsFilterBarProps {
    query: string;
    onQueryChange: (next: string) => void;
    sort: DashboardSort;
    onSortChange: (next: DashboardSort) => void;
    view: DashboardView;
    onViewChange: (next: DashboardView) => void;
}

interface FilterChipProps {
    label: string;
    value: string;
    active: boolean;
    onClick: () => void;
}

const FilterChip = ({ label, value, active, onClick }: FilterChipProps) => {
    const theme = useTheme();
    return (
        <Box
            component="button"
            type="button"
            onClick={onClick}
            sx={{
                appearance: "none",
                cursor: "pointer",
                fontFamily: "inherit",
                display: "inline-flex",
                alignItems: "center",
                gap: 0.75,
                height: 34,
                px: 1.5,
                borderRadius: 999,
                border: `1px solid ${active ? alpha(theme.palette.primary.main, 0.4) : theme.palette.divider}`,
                background: active ? alpha(theme.palette.primary.main, 0.07) : theme.palette.background.paper,
                color: active ? theme.palette.primary.main : theme.palette.text.secondary,
                fontSize: 12.5,
                fontWeight: 500,
                transition: "background .15s, border-color .15s, color .15s",
                "&:hover": {
                    borderColor: alpha(theme.palette.primary.main, 0.4),
                    background: alpha(theme.palette.primary.main, 0.07)
                }
            }}
        >
            <Typography component="span" sx={{ fontSize: 12.5, color: "inherit" }}>{label}</Typography>
            <Typography
                component="span"
                sx={{
                    fontSize: 12.5,
                    fontWeight: 600,
                    color: active ? theme.palette.primary.main : theme.palette.text.primary
                }}
            >
                {value}
            </Typography>
            <ExpandMoreIcon sx={{ fontSize: 14 }} />
        </Box>
    );
};

const ProductsFilterBar = ({
    query,
    onQueryChange,
    sort,
    onSortChange,
    view,
    onViewChange
}: ProductsFilterBarProps) => {
    const theme = useTheme();

    const handleSortClick = () => {
        const idx = SORT_CYCLE.indexOf(sort);
        const next = SORT_CYCLE[(idx + 1) % SORT_CYCLE.length];
        onSortChange(next);
    };

    return (
        <Stack
            direction={{ xs: "column", md: "row" }}
            alignItems={{ xs: "stretch", md: "center" }}
            spacing={1.25}
            sx={{ width: "100%" }}
        >
            <TextField
                value={query}
                onChange={(event) => onQueryChange(event.target.value)}
                placeholder="Search products, owners, features…"
                size="small"
                sx={{
                    flex: "1 1 280px",
                    maxWidth: { md: 360 },
                    "& .MuiOutlinedInput-root": {
                        height: 34,
                        fontSize: 13,
                        backgroundColor: theme.palette.background.paper,
                        "& fieldset": { borderColor: theme.palette.divider },
                        "&:hover fieldset": { borderColor: alpha(theme.palette.primary.main, 0.4) }
                    }
                }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
                        </InputAdornment>
                    ),
                    endAdornment: (
                        <InputAdornment position="end">
                            <Box
                                component="kbd"
                                sx={{
                                    fontFamily: '"JetBrains Mono", monospace',
                                    fontSize: 10.5,
                                    color: theme.palette.text.secondary,
                                    backgroundColor: theme.palette.grey[100],
                                    border: `1px solid ${theme.palette.divider}`,
                                    px: 0.75,
                                    py: 0.125,
                                    borderRadius: 1,
                                    lineHeight: 1.3
                                }}
                            >
                                ⌘ K
                            </Box>
                        </InputAdornment>
                    )
                }}
            />

            <FilterChip
                label="Sort"
                value={SORT_LABELS[sort]}
                active
                onClick={handleSortClick}
            />

            <FilterChip
                label="Integrations"
                value="All"
                active={false}
                onClick={() => undefined}
            />

            <Box sx={{ flex: 1 }} />

            <ToggleButtonGroup
                value={view}
                exclusive
                size="small"
                onChange={(_event, next: DashboardView | null) => {
                    if (next) onViewChange(next);
                }}
                sx={{
                    backgroundColor: theme.palette.grey[100],
                    borderRadius: 999,
                    p: 0.25,
                    "& .MuiToggleButton-root": {
                        border: "none",
                        borderRadius: "999px !important",
                        textTransform: "none",
                        px: 1.5,
                        py: 0.5,
                        fontSize: 12.5,
                        fontWeight: 500,
                        color: theme.palette.text.secondary,
                        gap: 0.5,
                        "&.Mui-selected": {
                            backgroundColor: theme.palette.background.paper,
                            color: theme.palette.primary.main,
                            boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
                            "&:hover": { backgroundColor: theme.palette.background.paper }
                        }
                    }
                }}
            >
                <ToggleButton value="grid" aria-label="Grid view">
                    <GridViewIcon sx={{ fontSize: 14, mr: 0.5 }} />
                    Grid
                </ToggleButton>
                <ToggleButton value="list" aria-label="List view">
                    <ViewListIcon sx={{ fontSize: 14, mr: 0.5 }} />
                    List
                </ToggleButton>
            </ToggleButtonGroup>
        </Stack>
    );
};

export default ProductsFilterBar;
